import Sentry from "@similarweb/sentry-logger";

import type { IFetchService } from "app/services/fetchService";
import { NOT_FOUND_ERROR } from "app/pages/activation/components/RankWidget/config";
import { ACTIVATION_API, RANK_TRACKER_API } from "app/pages/activation/services/constants";
import type {
    RTCampaignCreationDTO,
    RankTrackerCampaignsDTO,
    RankTrackerCountry,
    RankTrackerCampaign,
    KeywordTrendsCreationDTO,
    KeywordTrendsSchema,
    KeywordAvgCreationDTO,
    KeywordAvgSchema,
} from "app/pages/activation/types/api";

type RankTrackerApiSchema = {
    getCampaigns: () => Promise<RankTrackerCampaignsDTO>;
    getCampaign: (projectId: string) => Promise<RankTrackerCampaign>;
    getCountries: () => Promise<RankTrackerCountry[]>;
    getKeywordsTrends: (data: KeywordTrendsCreationDTO) => Promise<KeywordTrendsSchema>;
    getKeywordsAvg: (data: KeywordAvgCreationDTO) => Promise<KeywordAvgSchema>;
    createCampaign: (campaign: RTCampaignCreationDTO, projectId: string) => Promise<RankTrackerCampaignsDTO>;
    getIsEnoughCreditsToCreate: (projectId: string) => Promise<boolean>;
};

export const rankTrackerApiService = (fetchService: IFetchService): RankTrackerApiSchema => ({
    getCampaigns: async () => {
        try {
            return await fetchService.get(`${RANK_TRACKER_API}/campaigns`);
        } catch (error) {
            Sentry.error("Error fetching Rank Tracker campaigns:", error);
            throw new Error("Failed to fetch Rank Tracker campaigns");
        }
    },

    getCampaign: async (projectId) => {
        try {
            return await fetchService.get(`${ACTIVATION_API}/${projectId}/rankTracker/campaign`);
        } catch (error) {
            if (error?.status === 404) {
                throw new Error(NOT_FOUND_ERROR);
            }

            Sentry.error("Error fetching Rank Tracker campaign:", error);
            throw new Error(`Failed to fetch Rank Tracker campaign`);
        }
    },

    getCountries: async () => {
        try {
            return await fetchService.get(`${ACTIVATION_API}/rankTracker/countries`);
        } catch (error) {
            Sentry.error("Error fetching Rank Tracker countries:", error);
            throw new Error("Failed to fetch Rank Tracker countries");
        }
    },

    getKeywordsTrends: async (data) => {
        try {
            return await fetchService.get(`${RANK_TRACKER_API}/reports/keywordsTrendReport/UseCaseFiltersCounts`, data);
        } catch (error) {
            Sentry.error("Error fetching Rank Tracker keywords trends:", error);
            throw new Error("Failed to fetch Rank Tracker keywords trends");
        }
    },

    getKeywordsAvg: async (data: KeywordAvgCreationDTO) => {
        try {
            return await fetchService.get<KeywordAvgSchema>(`${RANK_TRACKER_API}/reports/overviewReport/Graph`, data);
        } catch (error) {
            Sentry.error("Error fetching Rank Tracker keywords avg:", error);
            throw new Error("Failed to fetch Rank Tracker keywords avg");
        }
    },

    createCampaign: async (campaign, projectId) => {
        try {
            return await fetchService.post(`${ACTIVATION_API}/${projectId}/rankTracker/campaign`, campaign);
        } catch (error) {
            Sentry.error("Error creating Rank Tracker campaign:", error);
            throw new Error("Failed to create Rank Tracker campaign");
        }
    },

    getIsEnoughCreditsToCreate: async (projectId) => {
        try {
            return await fetchService.get(
                `${ACTIVATION_API}/${projectId}/rankTracker/campaign/isEnoughCreditsToCreate`,
            );
        } catch (error) {
            Sentry.error("Error checking if allowed to create Rank Tracker campaign:", error);
            throw new Error("Failed to check if allowed to create Rank Tracker campaign");
        }
    },
});
